import { useState, useEffect, useRef, ChangeEvent, MouseEvent as ReactMouseEvent } from "react";
import cn from "classnames";
import { Button, TextArea } from "@/shared/ui";
import { MicrophoneIcon, SendIcon } from "@/shared/icons";
import { useStructurizeText, useTranscribeAudio } from "@/service/audio";

interface VoiceTextAreaProps {
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  assessmentId: number;
}

export const VoiceTextArea = ({ onChange, className, assessmentId }: VoiceTextAreaProps) => {
  const [feedback, setFeedback] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: transcribeAudio, isPending: isTranscribing } = useTranscribeAudio();
  const { mutate: structurizeText, isPending, error: assistantError } = useStructurizeText();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const shouldCancelRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (!isRecording) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") stopRecording(false);
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("button[data-voice-recorder]")) {
        stopRecording(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isRecording]);

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Audio recording is not supported in your browser.");
      return;
    }

    try {
      setError(null);
      setFeedback("");
      audioChunksRef.current = [];
      shouldCancelRef.current = false;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (shouldCancelRef.current) {
          audioChunksRef.current = [];
          shouldCancelRef.current = false;
          return;
        }

        if (audioChunksRef.current.length === 0) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        transcribeAudio(
          { audioBlob, assessmentId },
          {
            onSuccess: (text) => {
              structurizeText(text, {
                onSuccess: (data) => {
                  setFeedback(data);
                  onChange?.({ target: { value: data } } as ChangeEvent<HTMLTextAreaElement>);
                },
                onError: () => setFeedback(text),
              });
            },
            onError: (err: any) => {
              setError(err?.data?.message || "Failed to transcribe audio. Please try again.");
            },
          },
        );
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err: any) {
      console.error("Error starting recording:", err);
      if (err.name === "NotAllowedError") {
        setError("Microphone access was denied. Please allow microphone use in your browser settings.");
      } else if (err.name === "NotFoundError") {
        setError("Microphone not found or not working.");
      } else {
        setError("Failed to start recording. Please try again.");
      }
      setIsRecording(false);
    }
  };

  const stopRecording = (submit: boolean) => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;

    shouldCancelRef.current = !submit;
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const handleMicClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (isPending || isTranscribing) return;
    isRecording ? stopRecording(true) : startRecording();
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="relative">
        <TextArea
          value={feedback}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setFeedback(e.target.value);
            onChange?.(e);
          }}
          placeholder="Your feedback..."
          className="min-h-[280px] w-full resize-none p-3! pb-20! text-sm!"
          disabled={isRecording || isPending || isTranscribing}
        />
        <div className="absolute right-5 bottom-5 flex items-center gap-4 rounded-lg bg-transparent/80 pl-2 backdrop-blur-sm">
          <span className="text-xs text-gray-500">
            {isTranscribing
              ? "Transcribing audio..."
              : isPending
                ? "Analyzing your feedback"
                : isRecording
                  ? "Press ESC to cancel or click button to send"
                  : "Click to record voice memo"}
          </span>
          <div className="relative">
            {isRecording && <div className="animate-morph absolute inset-0 -m-1 bg-[#F8E8F7] opacity-75" />}
            <Button
              data-voice-recorder
              onClick={handleMicClick}
              disabled={isPending || isTranscribing}
              className={cn("relative h-12! w-12! rounded-full! border-[#ff8afb]/[0.12]!", {
                "bg-[#F8E8F7]! text-[#A52BA1]! hover:scale-105": !isRecording && !isPending && !isTranscribing,
                "bg-[#A52BA1]! text-white!": isRecording || isPending || isTranscribing,
                "opacity-75": isPending || isTranscribing,
              })}
            >
              {isPending || isTranscribing ? (
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
              ) : isRecording ? (
                <SendIcon size={20} />
              ) : (
                <MicrophoneIcon size={20} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-3">
        {(error || assistantError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
            {error || assistantError?.message}
          </div>
        )}
      </div>
    </div>
  );
};
